import {
  FILTER_PARAM_NAMES,
  clinicianSearchClearQueryParams,
} from 'client-portal/utils/clinicianSearch/facets/manifest';
import { PRESCREENER_RESET_OBJECT } from 'client-portal/utils/prescreener/constants';
import { STEP_IDS } from 'client-portal/services/widget-steps';
import { action, set } from '@ember/object';
import { bookingPreselectionQueryParamsFromClinicianSearch } from 'client-portal/utils/clinicianSearch/preselection';
import {
  parseCommaSeparatedInts,
  parseCommaSeparatedStrings,
} from 'client-portal/utils/clinicianSearch/comma-query-param';
import { reportClinicianSearchError } from 'client-portal/utils/clinicianSearch/report-error';
import { routeQueryParamsFor } from 'client-portal/utils/clinicianSearch/facets/query-param-aliases';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { waitForNext } from 'ember-simplepractice/utils/waiters';
import ENV from 'client-portal/config/environment';
import RequestBaseRoute from './base';
import uuidv4 from 'uuid/v4';

const CATALOG_SHOEBOX_KEY = 'clinician-search-catalog';
const EMPTY_ENRICHMENT_FILTERS = Object.freeze({
  officeIds: [],
  cptCodeRateId: null,
  weekdays: [],
  timeBuckets: [],
});

export default class RequestClinicianSearch extends RequestBaseRoute {
  @service store;
  @service request;
  @service clinicianSearch;
  @service pendingAppointment;
  @service router;
  @service fastboot;

  templateName = '-request/clinician-search';
  stepId = STEP_IDS.SELECT_CLINICIAN_SEARCH;
  _hasEnteredStep = false;
  _catalogShoeboxUnusable = false;

  beforeModel() {
    if (!this.clinicianSearch.isEnabled) {
      this.request.replaceWith(this, 'clinician');
    }
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    set(controller, 'nextStep', this.nextStep.bind(this));
  }

  resetController(controller, isExiting) {
    super.resetController(controller, isExiting);
    if (isExiting) {
      this._hasEnteredStep = false;
      controller.resetQueryParams();
    }
  }

  resolvedGlobalMonarchChannelId(params, reservation) {
    return (
      String(params.globalMonarchChannelId ?? '').trim() ||
      String(reservation?.get('channel.id') ?? '').trim() ||
      undefined
    );
  }

  queryParams = {
    pageNumber: { refreshModel: true },
    globalMonarchChannelId: { refreshModel: true },
    ...routeQueryParamsFor(FILTER_PARAM_NAMES, { refreshModel: true }),
  };

  @(task(function* (pageNumber) {
    if (!this.clinicianSearch.isEnabled) return;

    let loaded = yield this._loadCatalog();
    if (!loaded) return;

    // Client-only — FastBoot would tear down mid-batch.
    if (this.fastboot.isFastBoot) {
      return;
    }

    yield this._startClientEnrichment(pageNumber);
  }).restartable())
  loadCatalogTask;

  async _loadCatalog() {
    try {
      let shoeboxPayload = this._catalogShoeboxPayload();
      if (shoeboxPayload) {
        let hydrated = this.clinicianSearch.sendAll.hydrateFromStore({
          catalogErrorCode: shoeboxPayload.errorCode,
        });
        if (hydrated) {
          return true;
        }
      }
      await this.clinicianSearch.sendAll.ensureLoaded();
      return true;
    } catch (err) {
      reportClinicianSearchError(err);
      return false;
    }
  }

  _catalogShoeboxPayload() {
    if (this.fastboot.isFastBoot || this._catalogShoeboxUnusable) {
      return null;
    }
    return this.fastboot.shoebox?.retrieve(CATALOG_SHOEBOX_KEY) ?? null;
  }

  async _startClientEnrichment(pageNumber) {
    let routeParams = {
      ...this.clinicianSearch.query.toRouteParams(),
      pageNumber,
    };
    let filteredCohort = this.clinicianSearch.sendAll.filterForRouteParams(routeParams);
    let nextSlots = this.clinicianSearch.nextSlots;

    await nextSlots.enrichCohort(filteredCohort, { ...EMPTY_ENRICHMENT_FILTERS });

    let activeFilters = this._activeEnrichmentFilters(routeParams);
    if (!this._hasActiveAvailabilityFilters(activeFilters)) {
      return;
    }

    let rescueCandidates = this._rescueCandidates(filteredCohort);
    if (!rescueCandidates.length) {
      return;
    }

    // Re-enrich empty-block accepting clinicians dropped by filters.
    await nextSlots.enrichCohort(rescueCandidates, activeFilters);
  }

  _activeEnrichmentFilters(routeParams) {
    let officeIds = parseCommaSeparatedStrings(routeParams.officeIds);
    let weekdays = parseCommaSeparatedInts(routeParams.availableDays);
    let timeBuckets = parseCommaSeparatedInts(routeParams.availableTimes);
    let cptCodes = parseCommaSeparatedStrings(routeParams.cptCodes);

    return {
      officeIds,
      weekdays,
      timeBuckets,
      // next-slots accepts a single CPT.
      cptCodeRateId: cptCodes.length === 1 ? cptCodes[0] : null,
    };
  }

  _hasActiveAvailabilityFilters(filters) {
    return (
      filters.officeIds.length > 0 ||
      filters.weekdays.length > 0 ||
      filters.timeBuckets.length > 0 ||
      filters.cptCodeRateId !== null ||
      parseCommaSeparatedStrings(this.clinicianSearch.query.cptCodes).length > 0
    );
  }

  _rescueCandidates(filteredCohort) {
    let filteredIds = new Set(filteredCohort.map(record => String(record?.id ?? '')));
    return this.clinicianSearch.sendAll.records.filter(record => {
      let id = String(record?.id ?? '');
      if (!id || filteredIds.has(id)) {
        return false;
      }
      if (!record.acceptNewClientsIndicator) {
        return false;
      }
      let blocks = record.availabilityBlocks;
      return !Array.isArray(blocks) || blocks.length === 0;
    });
  }

  async model(params, transition) {
    let reservation = this.modelFor(this.request.baseRouteName);
    let urlParams = transition?.to?.queryParams ?? {};

    if (urlParams.preselected || urlParams.clinicianId) {
      await this.resetPreselection();
      return;
    }

    let pageSize = ENV.clinicianSearchPageSize;
    let pageNumber = parseInt(String(params.pageNumber ?? 1), 10);
    if (Number.isNaN(pageNumber) || pageNumber < 1) {
      pageNumber = 1;
    }

    this.clinicianSearch.query.syncFromRouteParams({
      ...params,
      globalMonarchChannelId: this.resolvedGlobalMonarchChannelId(params, reservation),
    });

    if (this.fastboot.isFastBoot) {
      // Await so FastBoot shoebox includes the catalog.
      await this.loadCatalogTask.perform(pageNumber);
    } else {
      this.loadCatalogTask.perform(pageNumber);
      await waitForNext();
    }

    return {
      pageSize,
    };
  }

  async resetPreselection() {
    await this.router.replaceWith({
      queryParams: clinicianSearchClearQueryParams(),
    });
  }

  afterModel() {
    super.afterModel(...arguments);
    this.pendingAppointment.reset();

    if (this.fastboot.isFastBoot) {
      this.fastboot.shoebox?.put(CATALOG_SHOEBOX_KEY, {
        errorCode: this.clinicianSearch.sendAll.catalogErrorCode,
      });
    }
  }

  @action
  async nextStep(clinician, transitionParams) {
    let reservation = this.modelFor(this.request.baseRouteName);
    if (!reservation) {
      return;
    }

    let queryParams = bookingPreselectionQueryParamsFromClinicianSearch({
      clinicianId: clinician.id,
      officeIds: this.clinicianSearch.query.officeIds,
      cptCodes: this.clinicianSearch.query.cptCodes,
      availableDays: this.clinicianSearch.query.availableDays,
      availableTimes: this.clinicianSearch.query.availableTimes,
      queryParams: transitionParams?.queryParams,
    });
    queryParams = this.clinicianSearch.preselection.start({
      reservation,
      clinicianId: clinician.id,
      queryParams,
    });

    let navParams = { ...(transitionParams ?? {}), queryParams };

    super.nextStep('clinician', clinician, 'service', navParams);

    let controller = this.controllerFor(this.request.baseRouteName);
    controller.updateNavigationSteps();
  }

  @action
  didTransition() {
    this._catalogShoeboxUnusable = true;

    let reservation = this.modelFor(this.request.baseRouteName);
    if (!reservation || this._hasEnteredStep) {
      return;
    }

    this._hasEnteredStep = true;

    reservation.setProperties({
      clinician: null,
      cptCode: null,
      office: null,
      startTime: null,
      clientType: null,
      message: null,
      hasCompletedMessageStep: false,
      channelFields: null,
      channelUploads: null,
      channelUploadsComplete: null,
      hasCompletedInformationStep: false,
      ...PRESCREENER_RESET_OBJECT,
      flowId: uuidv4(),
    });

    this._trackAppointmentStep(reservation);
    return true;
  }
}
